/**
 * Conflict Resolution Strategies for Offline Queue
 * Provides advanced conflict resolution for queued operations
 */

import { errorLogger } from './errorLogger';
import crypto from 'crypto-js';

/**
 * Generate operation fingerprint for duplicate detection
 */
export const generateOperationFingerprint = (operationType, userId, targetId, data) => {
  const fingerprintData = {
    type: operationType,
    user: userId,
    target: targetId,
    // For bookmarks: just the action
    // For claps: ignore (we merge these)
    // For comments: include content hash
    content: data?.content ? crypto?.SHA256(data?.content)?.toString() : null,
  };
  
  return crypto?.SHA256(JSON.stringify(fingerprintData))?.toString();
};

/**
 * Conflict Resolution Strategies
 */
export const CONFLICT_STRATEGIES = {
  /**
   * Last-Write-Wins Strategy
   * Compare timestamps and keep the newer version
   */
  lastWriteWins: (queuedData, serverData) => {
    const queuedTime = new Date(queuedData?.updated_at || queuedData?.created_at || queuedData?.timestamp)?.getTime();
    const serverTime = new Date(serverData?.updated_at || serverData?.created_at)?.getTime();

    if (isNaN(queuedTime) || isNaN(serverTime)) {
      console.warn('[ConflictResolution] Invalid timestamps, using queued data');
      return {
        shouldApply: true,
        data: queuedData,
        reason: 'invalid_timestamps',
      };
    }

    if (queuedTime > serverTime) {
      return {
        shouldApply: true,
        data: queuedData,
        reason: 'queued_newer',
        queuedTime,
        serverTime,
      };
    } else if (queuedTime < serverTime) {
      return {
        shouldApply: false,
        data: serverData,
        reason: 'server_newer',
        queuedTime,
        serverTime,
      };
    } else {
      // Same timestamp, prefer server
      return {
        shouldApply: false,
        data: serverData,
        reason: 'same_timestamp',
        queuedTime,
        serverTime,
      };
    }
  },

  /**
   * Server-State-Priority Merging
   * Fetch current server state and merge intelligently
   */
  serverStatePriority: async (queuedData, serverData, operationType) => {
    switch (operationType) {
      case 'bookmark':
        // For bookmarks, check if already exists
        if (serverData) {
          return {
            shouldApply: false,
            data: serverData,
            reason: 'bookmark_exists',
          };
        }
        return {
          shouldApply: true,
          data: queuedData,
          reason: 'bookmark_new',
        };

      case 'clap':
        // For claps, merge counts
        const serverClaps = serverData?.clap_count || 0;
        const queuedIncrement = queuedData?.clapIncrement || 1;
        return {
          shouldApply: true,
          data: {
            ...queuedData,
            clap_count: serverClaps + queuedIncrement,
          },
          reason: 'claps_merged',
          mergedCount: serverClaps + queuedIncrement,
        };

      case 'comment':
        // For comments, check for duplicates by content hash
        if (serverData) {
          const queuedContent = crypto?.SHA256(queuedData?.content || '')?.toString();
          const serverContent = crypto?.SHA256(serverData?.content || '')?.toString();
          
          if (queuedContent === serverContent) {
            return {
              shouldApply: false,
              data: serverData,
              reason: 'comment_duplicate',
            };
          }
        }
        return {
          shouldApply: true,
          data: queuedData,
          reason: 'comment_new',
        };

      case 'story_update':
        // For story updates, merge non-conflicting fields
        if (serverData) {
          const merged = { ...serverData };
          
          // Fields that can be merged (additive)
          const mergeableFields = ['tags', 'categories'];
          
          // Fields that use last-write-wins
          const conflictFields = ['title', 'content', 'excerpt', 'status'];
          
          // Merge mergeable fields
          mergeableFields?.forEach(field => {
            if (queuedData?.[field] && serverData?.[field]) {
              // Merge arrays
              if (Array.isArray(queuedData?.[field])) {
                merged[field] = [...new Set([...serverData[field], ...queuedData[field]])];
              }
            } else if (queuedData?.[field]) {
              merged[field] = queuedData?.[field];
            }
          });
          
          // Apply last-write-wins for conflict fields
          const lwwResult = CONFLICT_STRATEGIES?.lastWriteWins(queuedData, serverData);
          if (lwwResult?.shouldApply) {
            conflictFields?.forEach(field => {
              if (queuedData?.[field] !== undefined) {
                merged[field] = queuedData?.[field];
              }
            });
          }
          
          return {
            shouldApply: true,
            data: merged,
            reason: 'story_merged',
            lwwResult,
          };
        }
        return {
          shouldApply: true,
          data: queuedData,
          reason: 'story_new',
        };

      default:
        return {
          shouldApply: true,
          data: queuedData,
          reason: 'unknown_type',
        };
    }
  },

  /**
   * Merge Numeric Fields Strategy
   * For numeric fields like counts, merge by addition
   */
  mergeNumericFields: (queuedData, serverData, numericFields = []) => {
    const merged = { ...serverData };
    
    numericFields?.forEach(field => {
      const queuedValue = queuedData?.[field] || 0;
      const serverValue = serverData?.[field] || 0;
      merged[field] = serverValue + queuedValue;
    });
    
    return {
      shouldApply: true,
      data: merged,
      reason: 'numeric_merged',
    };
  },

  /**
   * Skip Duplicates Strategy
   * Check for duplicate operations and skip
   */
  skipDuplicates: (operation, existingOperations) => {
    const fingerprint = generateOperationFingerprint(
      operation?.operationType,
      operation?.data?.user_id || operation?.data?.author_id,
      operation?.data?.story_id || operation?.data?.id,
      operation?.data
    );

    const duplicate = existingOperations?.find(op => {
      const opFingerprint = generateOperationFingerprint(
        op?.operationType,
        op?.data?.user_id || op?.data?.author_id,
        op?.data?.story_id || op?.data?.id,
        op?.data
      );
      return opFingerprint === fingerprint && op?.id !== operation?.id;
    });

    if (duplicate) {
      return {
        isDuplicate: true,
        duplicateOf: duplicate?.id,
        action: 'skip',
      };
    }

    return {
      isDuplicate: false,
      action: 'proceed',
    };
  },
};

/**
 * Detect conflicts between queued operation and server state
 */
export const detectConflict = (queuedData, serverData, operationType) => {
  if (!serverData) {
    return {
      hasConflict: false,
      type: 'no_server_data',
    };
  }

  // Check timestamp conflict
  const queuedTime = new Date(queuedData?.updated_at || queuedData?.created_at || queuedData?.timestamp)?.getTime();
  const serverTime = new Date(serverData?.updated_at || serverData?.created_at)?.getTime();

  if (!isNaN(queuedTime) && !isNaN(serverTime) && serverTime > queuedTime) {
    return {
      hasConflict: true,
      type: 'timestamp',
      queuedTime,
      serverTime,
      timeDiff: serverTime - queuedTime,
    };
  }

  // Check version conflict (if version field exists)
  if (queuedData?.version && serverData?.version) {
    if (serverData?.version > queuedData?.version) {
      return {
        hasConflict: true,
        type: 'version',
        queuedVersion: queuedData?.version,
        serverVersion: serverData?.version,
      };
    }
  }

  // Check content conflict for specific types
  if (operationType === 'story_update') {
    const conflictFields = ['title', 'content', 'status'];
    const conflicts = [];

    conflictFields?.forEach(field => {
      if (queuedData?.[field] && serverData?.[field] && queuedData?.[field] !== serverData?.[field]) {
        conflicts?.push(field);
      }
    });

    if (conflicts?.length > 0) {
      return {
        hasConflict: true,
        type: 'content',
        conflictingFields: conflicts,
      };
    }
  }

  return {
    hasConflict: false,
    type: 'none',
  };
};

/**
 * Check if operation is stale
 */
export const isOperationStale = (operation, serverData, staleThresholdMs = 300000) => {
  // 5 minutes default threshold
  const operationTime = new Date(operation?.timestamp)?.getTime();
  const currentTime = Date.now();
  const age = currentTime - operationTime;

  if (age > staleThresholdMs) {
    return {
      isStale: true,
      reason: 'age',
      age,
      threshold: staleThresholdMs,
    };
  }

  // Check version mismatch
  if (operation?.version && serverData?.version) {
    if (Math.abs(serverData?.version - operation?.version) > 5) {
      return {
        isStale: true,
        reason: 'version_mismatch',
        queuedVersion: operation?.version,
        serverVersion: serverData?.version,
      };
    }
  }

  return {
    isStale: false,
  };
};

/**
 * Merge duplicate operations
 */
export const mergeDuplicateOperations = (operations) => {
  const merged = [];
  const fingerprints = new Map();

  operations?.forEach(op => {
    const fingerprint = generateOperationFingerprint(
      op?.operationType,
      op?.data?.user_id || op?.data?.author_id,
      op?.data?.story_id || op?.data?.id,
      op?.data
    );

    if (fingerprints?.has(fingerprint)) {
      const existing = fingerprints?.get(fingerprint);
      
      // Merge based on operation type
      if (op?.operationType === 'clap') {
        // Combine clap counts
        existing.metadata.clapIncrement = (existing?.metadata?.clapIncrement || 1) + (op?.metadata?.clapIncrement || 1);
      } else if (op?.operationType === 'bookmark') {
        // Keep latest bookmark state
        if (op?.timestamp > existing?.timestamp) {
          fingerprints?.set(fingerprint, op);
        }
      } else if (op?.operationType === 'comment') {
        // Skip duplicate comments (keep first)
        // Do nothing
      } else {
        // For other types, keep latest
        if (op?.timestamp > existing?.timestamp) {
          fingerprints?.set(fingerprint, op);
        }
      }
    } else {
      fingerprints?.set(fingerprint, op);
    }
  });

  return Array.from(fingerprints?.values());
};

/**
 * Log conflict resolution decision
 */
export const logConflictResolution = (operation, resolution, conflict) => {
  const logEntry = {
    timestamp: Date.now(),
    operationId: operation?.id,
    operationType: operation?.operationType,
    resolution: resolution?.reason,
    conflict: conflict?.type,
    shouldApply: resolution?.shouldApply,
    details: {
      queuedTime: resolution?.queuedTime,
      serverTime: resolution?.serverTime,
      conflictingFields: conflict?.conflictingFields,
    },
  };

  // Log to error logger
  errorLogger?.logError(
    new Error('Conflict Resolution'),
    {
      component: 'OfflineQueueManager',
      operation: 'conflictResolution',
      ...logEntry,
    }
  );

  // Store in localStorage for admin dashboard
  try {
    const logs = JSON.parse(localStorage.getItem('quilline_conflict_logs') || '[]');
    logs?.push(logEntry);
    
    // Keep only last 100 logs
    if (logs?.length > 100) {
      logs?.splice(0, logs?.length - 100);
    }
    
    localStorage.setItem('quilline_conflict_logs', JSON.stringify(logs));
  } catch (error) {
    console.error('[ConflictResolution] Failed to store conflict log:', error);
  }

  console.log('[ConflictResolution] Resolution logged:', logEntry);
};

/**
 * Get conflict logs for admin dashboard
 */
export const getConflictLogs = () => {
  try {
    return JSON.parse(localStorage.getItem('quilline_conflict_logs') || '[]');
  } catch (error) {
    console.error('[ConflictResolution] Failed to retrieve conflict logs:', error);
    return [];
  }
};

/**
 * Clear conflict logs
 */
export const clearConflictLogs = () => {
  try {
    localStorage.removeItem('quilline_conflict_logs');
    console.log('[ConflictResolution] Conflict logs cleared');
  } catch (error) {
    console.error('[ConflictResolution] Failed to clear conflict logs:', error);
  }
};